import React, { useState } from "react";
import classNames from "classnames/bind";
import { useEffect } from "react";

import { ReactComponent as Ration } from "../../icons/welcome_ration.svg";
import { ReactComponent as Schedule } from "../../icons/welcome_schedule.svg";
import { ReactComponent as Abort } from "../../icons/welcome_abort.svg";
import { ReactComponent as Trust } from "../../icons/welcome_trust.svg";
import { ReactComponent as Time } from "../../icons/welcome_time.svg";

import styles from "./WelcomeCarousel.module.css";
import { UserType } from "types/UserType";
import { DietanamnezType } from "types/DietanamnezType";
import { useStateManager } from "utils/useStateManager";

const cx = classNames.bind(styles);

type Props = {
  userType: UserType;
  dietanamnezType: DietanamnezType;
  carouselType: "WELCOME_REGISTRY" | "WELCOME_DIETANAMNEZ" | "FINISH_REGISTRY" | "BACK";
};

const userLabels = {
  [UserType.LITE]: {
    name: "вашу рекомендацию",
    duration: "72",
    shortName: "рекомендацию",
  },
  [UserType.PRO]: {
    name: "ваш рацион",
    duration: "24",
    shortName: "ваш рацион",
  },
};

const dietanamnezLabels = {
  [DietanamnezType.LONG]: {
    products: 100,
    groups: 13,
    duration: 20,
  },
  [DietanamnezType.SHORT]: {
    products: 36,
    groups: 7,
    duration: 10,
  },
};

export const WelcomeCarousel: React.FC<Props> = ({
  userType,
  dietanamnezType,
  carouselType,
}) => {
  const [slide, setSlide] = useState<number>(0);
  const { type, linkInfo } = useStateManager();
  const isEpharmacy = linkInfo?.custom?.custom_генерировать_бад_рацион_при_саморегистрации;

  const labels = userLabels[userType || UserType.LITE];
  const da = dietanamnezLabels[dietanamnezType || DietanamnezType.SHORT];

  let welcomeSlides = [
    {
      title: "пожалуйста, отвечайте искренне",
      text: `И как можно точнее, но доля погрешности вполне допустима. Помогите составить ${labels.name} максимально индивидуально.`,
      icon: <Trust height={40} />,
    },
    {
      title: `опрос действует только ${labels.duration} часа`,
      text: "Не откладывайте надолго, опрос не займет много времени. Постарайтесь ответить на все задаваемые вопросы.",
      icon: <Time height={40} />,
    },
    {
      title: "Нужно прерваться?",
      text: "Не волнуйтесь, просто откройте ссылку снова и вы продолжите с того же места. Все предыдущие ответы будут сохранены.",
      icon: <Abort height={40} />,
    },
  ];

  if (type === "REGISTRATION") {
    welcomeSlides = [
      {
        title: "пожалуйста, отвечайте искренне",
        text: `И как можно точнее, но доля погрешности вполне допустима. Помогите составить ${labels.name} максимально индивидуально.`,
        icon: <Trust height={40} />,
      },
      {
        title: "Нужно прерваться?",
        text: "Не волнуйтесь, просто откройте ссылку снова и вы продолжите с того же места. Все предыдущие ответы будут сохранены.",
        icon: <Abort height={40} />,
      },
    ];
  }
  
  if (isEpharmacy) {
    welcomeSlides = [
      {
        title: "пожалуйста, отвечайте искренне",
        text: `И как можно точнее, но доля погрешности вполне допустима. Помогите составить ${labels.name} максимально индивидуально.`,
        icon: <Trust height={40} />,
      },
    ];
  } 

  const registrySlides = [
    {
      title: `помогите составить ${labels.shortName}`,
      text: "Опрос даст представление о ваших пищевых привычках и оценить риски нарушений нутриентного баланса.",
      icon: <Ration height={40} />,
    },
    {
      title: `опрос займет не более ${da.duration} минут`,
      text: `Мы спросим вас о ${da.products} продуктах в ${da.groups} группах. Простые вопросы о частоте и объеме употребляемой пищи и ваших предпочтениях.`,
      icon: <Schedule height={40} />,
    },
  ];

  const getSlides = () => {
    if (carouselType === "WELCOME_REGISTRY") {
      return welcomeSlides;
    }

    if (carouselType === "FINISH_REGISTRY") {
      return registrySlides;
    }

    if (carouselType === "BACK") {
      const text =type === "REGISTRATION"
        ? `Постарайтесь ответить на все вопросы по группам продуктов.`
        : `Опрос действует только ${labels.duration} часа и не займет много времени. Постарайтесь ответить на все вопросы по группам продуктов.`

      return [
        {
          title: `Не откладывайте надолго`,
          text,
          icon: <Time height={40} />,
        },
      ];
    }

    return [...welcomeSlides, ...registrySlides];
  };

  const slides = getSlides();

  useEffect(() => {
    const interval = setTimeout(() => {
      setSlide(slide === slides.length - 1 ? 0 : slide + 1);
    }, 5000);

    return () => clearTimeout(interval);
  }, [slide, slides.length]);

  return (
    <article className={cx("container")}>
      <ul
        className={cx("slides")}
        style={{
          transform: `translate3d(-${slide * 100}%, 0, 0)`,
        }}
      >
        {slides.map((item, index) => (
          <li className={cx("slide")} key={index}>
            <div className={cx("icon")}>{item.icon}</div>
            <div className={cx("title")}>{item.title}</div>
            <div className={cx("text")}>{item.text}</div>
          </li>
        ))}
      </ul>
      {slides.length > 1 && (
        <ul className={cx("dots")}>
          {slides.map((item, index) => (
            <li className={cx("dot")}>
              <button
                type="button"
                onClick={() => setSlide(index)}
                className={cx("dotButton", {
                  dotButton_active: index === slide,
                })}
              >
                Выбрать Слайд {index + 1}
              </button>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
};
